import React, { useState, useEffect } from 'react';
import { api, downloadExport } from './App';
import { Check, Download } from './Icons';
import SpreadsheetImport from './SpreadsheetImport';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];
const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];
const DEFAULT_STATUS_COLORS = ['#F3F4F6', '#DBEAFE', '#FEF3C7', '#D1FAE5', '#FEE2E2'];
function getDefaultColor(idx) { return DEFAULT_STATUS_COLORS[idx] || '#F3F4F6'; }

const PLANNER_COLORS = [
  { key: 'color_module',             label: 'Module sessions',      default: '#3b82f6' },
  { key: 'color_mcq',                label: 'MCQ sessions',         default: '#f43f5e' },
  { key: 'color_test',               label: 'Test sessions',        default: '#14b8a6' },
  { key: 'color_recall',             label: 'Active Recall Blurt',  default: '#8b5cf6' },
  { key: 'color_review_needs_work',  label: 'Review: needs work',  default: '#e05555' },
  { key: 'color_review_moderate',    label: 'Review: moderate',    default: '#d4903a' },
];

const DASH_TOGGLES = [
  { key: 'dash_show_countdown',   label: 'Exam countdowns',         desc: 'Shows days remaining to your FLK1 and FLK2 exams' },
  { key: 'dash_show_calibration', label: 'Calibration alerts',      desc: 'Flags topics where your confidence and MCQ accuracy disagree by 20% or more' },
  { key: 'dash_show_checkin',     label: 'Recall nudge',            desc: "Suggests topics you haven't tested yourself on recently" },
  { key: 'dash_show_priority',    label: 'Priority topics',         desc: 'Your weakest FLK1 and FLK2 topics, ranked by urgency' },
  { key: 'dash_show_touched',     label: 'How are you feeling?',    desc: 'Quick confidence sliders for your most urgent topics' },
  { key: 'dash_show_mcq',         label: 'MCQ milestone',           desc: 'Progress bar showing how many MCQs you have completed' },
  { key: 'dash_show_legend',      label: 'Priority tier legend',    desc: 'Explains what each priority level means' },
  { key: 'dash_show_principle',   label: 'Revision principles',     desc: 'Tips on effective revision techniques' },
];

const TABS = [
  { key: 'account',  label: 'Account' },
  { key: 'study',    label: 'Study' },
  { key: 'display',  label: 'Display' },
  { key: 'data',     label: 'Data' },
  { key: 'billing',  label: 'Billing' },
];

function Toggle({ checked, onChange }) {
  return (
    <label className="settings-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-toggle-track" />
    </label>
  );
}

export default function Settings({ onRestartTour, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'account');
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState({});
  const [name, setName]       = useState('');
  const [flk1Date, setFlk1Date] = useState('');
  const [flk2Date, setFlk2Date] = useState('');
  const [provider, setProvider] = useState('');
  const [mcqTarget, setMcqTarget] = useState('40');
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [emailNew, setEmailNew] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwSaved, setPwSaved]     = useState(false);
  const [saved, setSaved] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [targetConf, setTargetConf] = useState('0.70');
  const [customTargetVal, setCustomTargetVal] = useState('');
  // Account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Status list (full ordered list stored in custom_statuses; empty = use defaults)
  const [allStatuses, setAllStatuses] = useState(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
  const [newStatus, setNewStatus] = useState('');
  // Spaced rep intervals
  const [srRedDays, setSrRedDays] = useState('7');
  const [srAmberDays, setSrAmberDays] = useState('14');
  // Billing
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  // Refund
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundWentWrong, setRefundWentWrong] = useState('');
  const [refundImprovement, setRefundImprovement] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundDone, setRefundDone] = useState(false);

  useEffect(() => {
    Promise.all([api('/api/settings'), api('/api/me')]).then(([s, u]) => {
      setSettings(s);
      setUser(u);
      setName(u.name || '');
      setFlk1Date(s.flk1_exam_date || (u.exam_date_flk1 ? u.exam_date_flk1.slice(0, 10) : '') || '');
      setFlk2Date(s.flk2_exam_date || (u.exam_date_flk2 ? u.exam_date_flk2.slice(0, 10) : '') || '');
      setProvider(s.material_provider || '');
      setMcqTarget(s.mcq_daily_target || '40');
      try {
        const cs = JSON.parse(s.custom_statuses || '[]');
        if (cs.length > 0) {
          // Support both string array and object array
          const normalized = cs.map((item, idx) =>
            typeof item === 'string' ? { label: item, color: getDefaultColor(idx) } : item
          );
          setAllStatuses(normalized);
        } else {
          setAllStatuses(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
        }
      } catch {
        setAllStatuses(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
      }
      setSrRedDays(s.spaced_rep_red_days || '7');
      setSrAmberDays(s.spaced_rep_amber_days || '14');
      setTargetConf(s.target_confidence || '0.70');
      // If stored target_confidence is not one of the presets, it's custom
      const presets = ['0.60', '0.70', '0.80'];
      const storedTarget = s.target_confidence || '0.70';
      if (!presets.includes(storedTarget)) {
        setCustomTargetVal(String(Math.round(parseFloat(storedTarget) * 100)));
      }
    });
  }, []);

  const flash = (key) => {
    setSaved(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 2000);
  };

  const saveSetting = async (key, value) => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ [key]: value }) });
    flash(key);
  };

  const saveName = async () => {
    if (!name.trim()) return;
    await api('/api/account', { method: 'PATCH', body: JSON.stringify({ name: name.trim() }) });
    flash('name');
  };

  const saveDates = async () => {
    const settingsUp = {};
    const accountUp = {};
    if (flk1Date) { settingsUp.flk1_exam_date = flk1Date; accountUp.exam_date_flk1 = flk1Date; }
    if (flk2Date) { settingsUp.flk2_exam_date = flk2Date; accountUp.exam_date_flk2 = flk2Date; }
    await Promise.all([
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUp) }),
      api('/api/account',  { method: 'PATCH', body: JSON.stringify(accountUp) }),
    ]);
    flash('dates');
  };

  const saveMcqTarget = () => {
    const v = Math.max(1, Math.min(999, parseInt(mcqTarget) || 40)).toString();
    setMcqTarget(v);
    saveSetting('mcq_daily_target', v);
  };

  const saveAllStatuses = (list) => {
    setAllStatuses(list);
    saveSetting('custom_statuses', JSON.stringify(list));
  };
  const moveStatusUp = (idx) => {
    if (idx === 0) return;
    const u = [...allStatuses];
    [u[idx - 1], u[idx]] = [u[idx], u[idx - 1]];
    saveAllStatuses(u);
  };
  const moveStatusDown = (idx) => {
    if (idx === allStatuses.length - 1) return;
    const u = [...allStatuses];
    [u[idx], u[idx + 1]] = [u[idx + 1], u[idx]];
    saveAllStatuses(u);
  };
  const removeStatus = (idx) => {
    saveAllStatuses(allStatuses.filter((_, i) => i !== idx));
  };
  const addStatus = () => {
    const s = newStatus.trim();
    if (!s || allStatuses.some(x => x.label === s)) return;
    saveAllStatuses([...allStatuses, { label: s, color: getDefaultColor(allStatuses.length) }]);
    setNewStatus('');
  };

  const saveSpacedRep = () => {
    const red = Math.max(1, Math.min(90, parseInt(srRedDays) || 7)).toString();
    const amber = Math.max(1, Math.min(90, parseInt(srAmberDays) || 14)).toString();
    setSrRedDays(red); setSrAmberDays(amber);
    saveSetting('spaced_rep_red_days', red);
    saveSetting('spaced_rep_amber_days', amber);
  };

  const updateEmail = async (e) => {
    e.preventDefault();
    if (!emailNew.trim() || !emailPw.trim()) return;
    setEmailSaving(true); setEmailError('');
    const res = await api('/api/update-email', {
      method: 'POST',
      body: JSON.stringify({ new_email: emailNew.trim(), password: emailPw.trim() }),
    });
    setEmailSaving(false);
    if (res.error) { setEmailError(res.error); }
    else {
      setUser(prev => ({ ...prev, email: emailNew.trim() }));
      setEmailSaved(true); setShowEmailEdit(false); setEmailNew(''); setEmailPw('');
      setTimeout(() => setEmailSaved(false), 3000);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    const res = await api('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
    });
    if (res.ok) {
      setPwSaved(true); setCurrentPw(''); setNewPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } else {
      setPwError(res.error || 'Failed to change password');
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || !deletePassword.trim()) return;
    setDeleting(true);
    setDeleteError('');
    const res = await api('/api/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: deleteConfirmText, password: deletePassword }),
    });
    setDeleting(false);
    if (res.deleted) {
      localStorage.removeItem('sqetrack_token');
      window.location.href = 'https://sqetrack.co.uk/?deleted=1';
    } else {
      setDeleteError(res.error || 'Could not delete account. Please try again.');
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const res = await api('/api/create-portal-session', { method: 'POST' });
      if (res.error) { setPortalError(res.error); }
      else if (res.url) { window.location.href = res.url; }
    } catch { setPortalError('Something went wrong. Please try again.'); }
    finally { setPortalLoading(false); }
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    // Min length validation
    if (refundReason.length > 0 && refundReason.length < 20) {
      setRefundError('Please provide at least 20 characters in the reason field.');
      return;
    }
    if (refundImprovement.length > 0 && refundImprovement.length < 20) {
      setRefundError('Please provide at least 20 characters in the improvement field.');
      return;
    }
    setRefundSubmitting(true);
    setRefundError('');
    const res = await api('/api/refund-request', {
      method: 'POST',
      body: JSON.stringify({ reason: refundReason, what_went_wrong: refundWentWrong, improvement: refundImprovement }),
    });
    setRefundSubmitting(false);
    if (res.error) { setRefundError(res.error); }
    else { setRefundDone(true); setShowRefundForm(false); }
  };

  if (showImport) {
    return <SpreadsheetImport onBack={() => setShowImport(false)} onDone={() => setShowImport(false)} />;
  }

  // Calculate refund eligibility
  const isLifetime = user.tier === 'tracker_practice' || user.tier === 'complete';
  const isMonthly = user.tier === 'monthly';
  const withinRefundWindow = isLifetime && user.paid_at
    ? (Date.now() - new Date(user.paid_at).getTime()) < 14 * 86400000
    : false;

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">Settings</div>
      </div>

      {/* ── Tab navigation ── */}
      <div className="settings-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`settings-tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ACCOUNT tab ── */}
      {activeTab === 'account' && (
        <>
          <div className="settings-section">
            <div className="settings-section-title">Account</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Display name</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <input className="settings-text-input" value={name}
                  onChange={e => setName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={e => e.key === 'Enter' && saveName()} />
                {saved['name'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Email</span>
              </div>
              <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="settings-muted-val">{user.email || ''}</span>
                  {!showEmailEdit && (
                    <button className="settings-action-btn" style={{ padding: '2px 10px', fontSize: 11 }}
                      onClick={() => { setShowEmailEdit(true); setEmailError(''); }}>
                      Edit
                    </button>
                  )}
                  {emailSaved && <span className="settings-inline-saved"><Check size={12} /> Updated</span>}
                </div>
                {showEmailEdit && (
                  <form onSubmit={updateEmail} style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', maxWidth: 280 }}>
                    <input className="settings-text-input" type="email" placeholder="New email address"
                      value={emailNew} onChange={e => setEmailNew(e.target.value)} autoFocus />
                    <input className="settings-text-input" type="password" placeholder="Current password"
                      value={emailPw} onChange={e => setEmailPw(e.target.value)} />
                    {emailError && <span className="settings-error">{emailError}</span>}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="settings-action-btn" type="button"
                        onClick={() => { setShowEmailEdit(false); setEmailError(''); setEmailNew(''); setEmailPw(''); }}>
                        Cancel
                      </button>
                      <button className="settings-save-btn" type="submit"
                        disabled={emailSaving || !emailNew.trim() || !emailPw.trim()}>
                        {emailSaving ? 'Saving…' : 'Save email'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
            <div className="settings-pw-block" style={{ paddingLeft: 'var(--sp-4)' }}>
              <span className="settings-row-label" style={{ marginBottom: 8, display: 'block' }}>Change password</span>
              <form className="settings-pw-form" onSubmit={changePassword}>
                <input className="settings-text-input" type="password" placeholder="Current password"
                  value={currentPw} onChange={e => setCurrentPw(e.target.value)} autoComplete="current-password" />
                <input className="settings-text-input" type="password" placeholder="New password (8+ characters)"
                  value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" />
                {pwError && <span className="settings-error">{pwError}</span>}
                {pwSaved && <span className="settings-inline-saved"><Check size={12} /> Password updated</span>}
                <button className="settings-save-btn" type="submit" style={{ alignSelf: 'flex-start' }}>
                  Update password
                </button>
              </form>
            </div>
          </div>

          <div className="settings-section settings-section--danger">
            <div className="settings-section-title" style={{ color: 'var(--red)' }}>Danger zone</div>
            {!showDeleteConfirm ? (
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-label">Delete my account</span>
                  <span className="settings-row-key">Permanently deletes your account and all revision data</span>
                </div>
                <div className="settings-row-right">
                  <button className="settings-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
                    Delete account
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 12, fontWeight: 600, lineHeight: 1.5 }}>
                  This permanently deletes your account and all revision data. This cannot be undone.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="settings-text-input" placeholder='Type "DELETE" to confirm'
                    value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
                  <input className="settings-text-input" type="password" placeholder="Enter your password"
                    value={deletePassword} onChange={e => setDeletePassword(e.target.value)} />
                  {deleteError && <span className="settings-error">{deleteError}</span>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="settings-action-btn" onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setDeletePassword('');
                      setDeleteError('');
                    }}>Cancel</button>
                    <button className="settings-delete-btn"
                      disabled={deleteConfirmText !== 'DELETE' || !deletePassword.trim() || deleting}
                      onClick={deleteAccount}>
                      {deleting ? 'Deleting…' : 'Permanently delete account'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── STUDY tab ── */}
      {activeTab === 'study' && (
        <>
          <div className="settings-section">
            <div className="settings-section-title">Exam dates</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">FLK1</span>
              </div>
              <div className="settings-row-right">
                <input className="settings-date-input" type="date" value={flk1Date}
                  onChange={e => setFlk1Date(e.target.value)} onBlur={saveDates} />
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">FLK2</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <input className="settings-date-input" type="date" value={flk2Date}
                  onChange={e => setFlk2Date(e.target.value)} onBlur={saveDates} />
                {saved['dates'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Study materials</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Provider</span>
                <span className="settings-row-key">Used for MCQ import guidance</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <select className="settings-select" value={provider}
                  onChange={e => { setProvider(e.target.value); saveSetting('material_provider', e.target.value); }}>
                  <option value="">— Not set —</option>
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {saved['material_provider'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">MCQ practice</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Daily MCQ target</span>
                <span className="settings-row-key">Questions you aim to do each day</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <input className="settings-number-input" type="number" min="1" max="999" value={mcqTarget}
                  onChange={e => setMcqTarget(e.target.value)}
                  onBlur={saveMcqTarget}
                  onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                {saved['mcq_daily_target'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Preparation target</div>
            <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' }}>
              <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
                Sets the confidence threshold that counts as "ready" in the planner. SQE1 is pass/fail — you don't need 100%, but you do need consistent accuracy across all areas.
              </p>
              <div className="prep-target-options">
                {[
                  { value: '0.60', label: 'Solid', desc: 'Confident on the essentials. Good baseline pass preparation.' },
                  { value: '0.70', label: 'Well prepared', desc: 'Strong across all areas. Recommended for most candidates.' },
                  { value: '0.80', label: 'Very prepared', desc: 'High confidence standard. For candidates aiming well above the pass mark.' },
                ].map(opt => (
                  <label key={opt.value} className={`prep-target-btn ${targetConf === opt.value ? 'selected' : ''}`}>
                    <input type="radio" name="target_conf" value={opt.value} checked={targetConf === opt.value}
                      onChange={() => { setTargetConf(opt.value); saveSetting('target_confidence', opt.value); }}
                      style={{ display: 'none' }} />
                    <span className="prep-target-label">{opt.label} <span className="prep-target-pct">{Math.round(parseFloat(opt.value) * 100)}%</span></span>
                    <span className="prep-target-desc">{opt.desc}</span>
                  </label>
                ))}
                <label key="custom" className={`prep-target-btn ${!['0.60','0.70','0.80'].includes(targetConf) ? 'selected' : ''}`}>
                  <input type="radio" name="target_conf" value="custom"
                    checked={!['0.60','0.70','0.80'].includes(targetConf)}
                    onChange={() => {
                      const v = customTargetVal || '65';
                      setCustomTargetVal(v);
                      const fv = (parseInt(v) / 100).toFixed(2);
                      setTargetConf(fv);
                      saveSetting('target_confidence', fv);
                    }}
                    style={{ display: 'none' }} />
                  <span className="prep-target-label">Custom <span className="prep-target-pct">{!['0.60','0.70','0.80'].includes(targetConf) ? Math.round(parseFloat(targetConf)*100) + '%' : ''}</span></span>
                  <span className="prep-target-desc">Set your own confidence threshold.</span>
                  {!['0.60','0.70','0.80'].includes(targetConf) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                      <input
                        type="number" min="50" max="95" className="settings-number-input"
                        value={customTargetVal}
                        onChange={e => setCustomTargetVal(e.target.value)}
                        onBlur={() => {
                          const v = Math.max(50, Math.min(95, parseInt(customTargetVal) || 65));
                          setCustomTargetVal(String(v));
                          const fv = (v / 100).toFixed(2);
                          setTargetConf(fv);
                          saveSetting('target_confidence', fv);
                        }}
                        style={{ width: 60 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
                    </div>
                  )}
                </label>
              </div>
              {saved['target_confidence'] && <span className="settings-inline-saved" style={{ marginTop: 8, display: 'inline-block' }}><Check size={12} /> Saved</span>}
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Topic statuses</div>
            <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' }}>
              <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
                Customise the statuses shown in your topic tracker. Reorder, remove, or add your own.
              </p>
              <div className="status-order-list">
                {allStatuses.map((statusObj, idx) => (
                  <div key={(statusObj.label || statusObj) + idx} className="status-order-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="color"
                        value={statusObj.color || getDefaultColor(idx)}
                        style={{ width: 22, height: 22, padding: 1, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', background: 'none' }}
                        onChange={e => {
                          const updated = allStatuses.map((x, i) => i === idx ? { ...x, color: e.target.value } : x);
                          saveAllStatuses(updated);
                        }}
                        title="Pick colour"
                      />
                      <span className="status-order-name">{statusObj.label || statusObj}</span>
                    </div>
                    <div className="status-order-controls">
                      <button className="status-order-btn" onClick={() => moveStatusUp(idx)} disabled={idx === 0} title="Move up">↑</button>
                      <button className="status-order-btn" onClick={() => moveStatusDown(idx)} disabled={idx === allStatuses.length - 1} title="Move down">↓</button>
                      <button className="status-order-btn status-order-remove" onClick={() => removeStatus(idx)} title="Remove">×</button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input className="settings-text-input" value={newStatus} placeholder="Add a status…"
                  onChange={e => setNewStatus(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addStatus()}
                  style={{ width: 160 }} />
                <button className="settings-action-btn" onClick={addStatus} disabled={!newStatus.trim()}>Add</button>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Spaced repetition</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Weak topics (under 50%)</span>
                <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <input className="settings-number-input" type="number" min="1" max="90" value={srRedDays}
                  onChange={e => setSrRedDays(e.target.value)}
                  onBlur={saveSpacedRep}
                  onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Moderate topics (50–69%)</span>
                <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
              </div>
              <div className="settings-row-right" style={{ gap: 8 }}>
                <input className="settings-number-input" type="number" min="1" max="90" value={srAmberDays}
                  onChange={e => setSrAmberDays(e.target.value)}
                  onBlur={saveSpacedRep}
                  onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
                {saved['spaced_rep_red_days'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Planner</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Study session level</span>
                <span className="settings-row-key">Choose whether planned sessions target broad topics or specific subtopics.</span>
              </div>
              <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                {[
                  { value: 'L2', label: 'General topics', desc: 'Study Negligence as a whole (recommended)' },
                  { value: 'L3', label: 'Subtopics', desc: 'Focus on Duty of Care specifically' },
                ].map(opt => (
                  <label key={opt.value} className="settings-radio-label">
                    <input
                      type="radio"
                      name="planner_detail_level"
                      value={opt.value}
                      checked={(settings.planner_detail_level || 'L2') === opt.value}
                      onChange={() => { setSettings(prev => ({ ...prev, planner_detail_level: opt.value })); saveSetting('planner_detail_level', opt.value); }}
                    />
                    <span className="settings-radio-text">
                      <span className="settings-radio-name">{opt.label}</span>
                      <span className="settings-radio-desc">{opt.desc}</span>
                    </span>
                  </label>
                ))}
                {saved['planner_detail_level'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── DISPLAY tab ── */}
      {activeTab === 'display' && (
        <>
          <div className="settings-section">
            <div className="settings-section-title">Planner colours</div>
            {PLANNER_COLORS.map(({ key, label, default: def }) => (
              <div key={key} className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-label">{label}</span>
                </div>
                <div className="settings-row-right" style={{ gap: 10 }}>
                  <input
                    type="color"
                    value={settings[key] || def}
                    className="settings-color-input"
                    onChange={e => {
                      setSettings(prev => ({ ...prev, [key]: e.target.value }));
                      saveSetting(key, e.target.value);
                    }}
                  />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--hint)' }}>
                    {settings[key] || def}
                  </span>
                  {settings[key] && settings[key] !== def && (
                    <button className="settings-action-btn" style={{ padding: '2px 8px', fontSize: 11 }}
                      onClick={() => { setSettings(prev => ({ ...prev, [key]: def })); saveSetting(key, def); }}>
                      Reset
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Dashboard panels</div>
            {DASH_TOGGLES.map(({ key, label, desc }) => (
              <div key={key} className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-label">{label}</span>
                  {desc && <span className="settings-row-key">{desc}</span>}
                </div>
                <div className="settings-row-right">
                  <Toggle
                    checked={(settings[key] ?? '1') === '1'}
                    onChange={e => {
                      const val = e.target.checked ? '1' : '0';
                      setSettings(prev => ({ ...prev, [key]: val }));
                      saveSetting(key, val);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── DATA tab ── */}
      {activeTab === 'data' && (
        <>
          <div className="settings-section">
            <div className="settings-section-title">Data</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Export your data</span>
                <span className="settings-row-key">Downloads all topics and MCQ history as Excel</span>
              </div>
              <div className="settings-row-right">
                <button className="settings-action-btn" onClick={downloadExport}>
                  <Download size={13} /> Export to Excel
                </button>
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Import data</span>
                <span className="settings-row-key">Import confidence scores from a spreadsheet</span>
              </div>
              <div className="settings-row-right">
                <button className="settings-action-btn" onClick={() => setShowImport(true)}>
                  Import spreadsheet
                </button>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-title">Testing</div>
            <div className="settings-info-row">
              <span className="settings-info-label">New user preview</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <button className="settings-newuser-btn"
                  onClick={async () => {
                    await api('/api/debug/reset-new-user', { method: 'POST' });
                    window.location.reload();
                  }}>
                  Preview as new user →
                </button>
                <span style={{ fontSize: 11, color: 'var(--hint)' }}>Resets onboarding flags only. All data is kept.</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── BILLING tab ── */}
      {activeTab === 'billing' && (
        <>
          <div className="settings-section">
            <div className="settings-section-title">Your plan</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Current plan</span>
              </div>
              <div className="settings-row-right">
                <span className="settings-muted-val">
                  {user.tier === 'monthly' ? 'Monthly — £9.99/mo' :
                   (user.tier === 'tracker_practice' || user.tier === 'complete') ? 'Tracker + Practice — one-time' :
                   user.tier || 'Unknown'}
                </span>
              </div>
            </div>
            {user.paid_at && (
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-label">Purchased</span>
                </div>
                <div className="settings-row-right">
                  <span className="settings-muted-val">{new Date(user.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
            )}
          </div>

          {isMonthly && (
            <div className="settings-section">
              <div className="settings-section-title">Subscription</div>
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-label">Manage subscription</span>
                  <span className="settings-row-key">Update payment method, cancel, or view invoices</span>
                </div>
                <div className="settings-row-right">
                  <button className="settings-action-btn" onClick={openPortal} disabled={portalLoading}>
                    {portalLoading ? 'Opening…' : 'Manage subscription →'}
                  </button>
                </div>
              </div>
              {portalError && <div className="settings-error" style={{ padding: '8px 16px' }}>{portalError}</div>}
            </div>
          )}

          {isLifetime && (
            <div className="settings-section">
              <div className="settings-section-title">Refund</div>
              {refundDone ? (
                <div style={{ padding: 'var(--sp-3) var(--sp-4)', color: 'var(--green)', fontSize: 13, fontWeight: 500 }}>
                  <Check size={13} style={{ marginRight: 4 }} /> Refund request submitted. We'll review it within 1–2 business days.
                </div>
              ) : withinRefundWindow ? (
                <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                  <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
                    You're within the 14-day refund window. If SQETrack isn't working for you, we'll refund your purchase in full.
                  </p>
                  {!showRefundForm ? (
                    <button className="settings-action-btn" onClick={() => setShowRefundForm(true)}>
                      Request a refund
                    </button>
                  ) : (
                    <form onSubmit={submitRefund} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>What's the main reason for your refund request?</label>
                        <textarea className="settings-text-input" rows={2} value={refundReason}
                          onChange={e => setRefundReason(e.target.value)} required
                          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                        {refundReason.length > 0 && refundReason.length < 20 && (
                          <span style={{ fontSize: 11, color: 'var(--amber)' }}>Please provide at least 20 characters so we can understand your experience.</span>
                        )}
                      </div>
                      <div>
                        <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>What specifically wasn't working for you?</label>
                        <textarea className="settings-text-input" rows={2} value={refundWentWrong}
                          onChange={e => setRefundWentWrong(e.target.value)}
                          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                      </div>
                      <div>
                        <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>Is there anything we could have done better?</label>
                        <textarea className="settings-text-input" rows={2} value={refundImprovement}
                          onChange={e => setRefundImprovement(e.target.value)}
                          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                        {refundImprovement.length > 0 && refundImprovement.length < 20 && (
                          <span style={{ fontSize: 11, color: 'var(--amber)' }}>Please provide at least 20 characters so we can understand your experience.</span>
                        )}
                      </div>
                      {refundError && <div className="settings-error">{refundError}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="settings-action-btn" type="button" onClick={() => setShowRefundForm(false)}>Cancel</button>
                        <button className="settings-save-btn" type="submit" disabled={refundSubmitting || !refundReason.trim() || (refundReason.length < 20) || (refundImprovement.length > 0 && refundImprovement.length < 20)}>
                          {refundSubmitting ? 'Submitting…' : 'Submit refund request'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              ) : (
                <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                  <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                    The 14-day refund window has passed. If you have concerns, email <a href="mailto:hello@sqetrack.co.uk">hello@sqetrack.co.uk</a>.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
